import React, {useEffect, useState} from 'react'
import {PlantCollection, PlantWithProjectName} from '../../entities/plant'
import {instance as plantsService} from '../../services/PlantsService'
import {instance as eventsService} from '../../services/PlantEventsJournalService'
import {useIntl} from 'react-intl'
import {Button, Col, Divider, message, Row, Spin} from 'antd'
import {
  DeleteOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  EditOutlined,
} from '@ant-design/icons'
import {PlantFormViewContent} from './PlantFormViewContent'
import {PlantFormEditContent} from './PlantFormEditContent'
import {useLocation, useRevalidator, useRouteLoaderData, useSubmit} from 'react-router-dom'
import {ConfirmWithCheckModal} from './components/ConfirmWithCheckModal'
import {PlantTimeEvents} from './components/PlantTimeEvents'
import {PlantRouteLoaderData} from '../App'

export async function getBasePlantFromDb(id?: string) {
  if (!id) {
    return {}
  }

  const plantFromDatabaseAns = await plantsService.getPlantFromPlantDatabaseById(id)

  if (plantFromDatabaseAns.status === 'found') {
    return plantFromDatabaseAns.data.fields
  }

  return {}
}

export type PlantViewProps = {
  plantData: PlantRouteLoaderData
  isLoading: boolean
  onClose?: () => void
  preventFormClose: (reasonMessage: string | null) => void
}

export function PlantView({
  plantData,
  isLoading,
  onClose = () => {},
  preventFormClose,
}: PlantViewProps) {
  const {
    plant = null,
    plantTitlePhoto = null,
    userPlantCollections = [],
    plantTimeEvents = [],
    plantTasks: plantToDos = [],
    userToDoTypes = [],
    userEventTypes = [],
  } = plantData

  const {projectNames = []} = useRouteLoaderData('root') as {
    projectNames: {id: number; name: string}[]
  }

  const [mode, setMode] = useState<'view' | 'edit' | 'create'>(!plant ? 'create' : 'view')
  const intl = useIntl()
  const [messageApi, contextHolder] = message.useMessage()
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false)
  const revalidator = useRevalidator()
  const submit = useSubmit()

  useEffect(() => {
    preventFormClose(null)
  }, [mode])

  const [timelineCollapsed, setTimelineCollapsed] = useState(false)

  const onOkDelete = async (isDeletePlantEvents: boolean) => {
    // if (isDeletePlantEvents) {
    //   const resultEventsDel = await eventsService.deleteAllPlantEventsForPlant(plant.id)
    //   if (resultEventsDel.success) {
    //     revalidator.revalidate()
    //   } else {
    //     messageApi.open({
    //       type: 'error',
    //       content: resultEventsDel.error?.message,
    //     })
    //   }
    // }
    const result = await plantsService.deletePlant(plant.id, isDeletePlantEvents, plant.projectId)
    if (result.success) {
      setOpenDeleteModal(false)
      submit({}, {method: 'delete'})
    } else {
      messageApi.open({
        type: 'error',
        content: result.error?.message,
      })
    }
  }

  useEffect(() => {
    if (plant) {
      setMode('view')
    } else {
      setMode('create')
    }
  }, [plant?.id])

  const titles: Record<typeof mode, string> = {
    create: intl.formatMessage({id: 'addingNewPlant'}),
    edit: intl.formatMessage({id: 'editingPlant'}),
    view: plant?.name,
  }

  return (
    <div>
      <Spin spinning={isLoading}>
        {mode === 'view' && plant && (
          <h2 className="plant-view__header">
            <span>{titles[mode]}</span>
            {plant.isPlantDeleted ? (
              <span className="deleted_plant">
                ({intl.formatMessage({id: 'thePlantWasRemovedByTheUser'})})
              </span>
            ) : (
              <span className="plant-view__header__actions">
                <Button
                  className="plant-view__header__edit"
                  icon={<EditOutlined />}
                  onClick={() => setMode('edit')}
                >
                  {intl.formatMessage({id: 'editPlant'})}
                </Button>

                <Button danger icon={<DeleteOutlined />} onClick={() => setOpenDeleteModal(true)}>
                  {intl.formatMessage({id: 'deletePlant'})}
                </Button>
              </span>
            )}
          </h2>
        )}
        <ConfirmWithCheckModal
          checkboxLabel={intl.formatMessage({id: 'deletePlantEventsConfirm'})}
          label={`${intl.formatMessage({id: 'deletePlantConfirm'})} ${intl.formatMessage({
            id: 'deletePlantFromToDoConfirm',
          })}`}
          isOpen={isOpenDeleteModal}
          onCancel={() => setOpenDeleteModal(false)}
          onOk={onOkDelete}
        />

        <Row className="plant-form-container">
          <Col className="main-column">
            {(mode === 'edit' || mode === 'create') && (
              <div>
                <PlantFormEditContent
                  currentPlant={plant}
                  setMode={setMode}
                  plantTitlePhoto={plantTitlePhoto}
                  projectsList={projectNames}
                  onClose={onClose}
                  preventFormClose={preventFormClose}
                  mode={mode}
                />
              </div>
            )}
            {mode === 'view' && plant && (
              <div className="plant-view">
                <div className="plant-view__content">
                  <PlantFormViewContent
                    currentPlant={plant}
                    plantTitlePhoto={plantTitlePhoto}
                    projectsList={projectNames}
                    userPlantCollections={userPlantCollections}
                  />
                </div>
                {!plant.gardenPlanVariantId && (
                  <>
                    <Divider
                      className="plant-view__divider"
                      orientation="vertical"
                      titlePlacement="left"
                    ></Divider>
                    <div
                      className={
                        timelineCollapsed
                          ? 'plant-view__timeline plant-view__timeline--collapsed'
                          : 'plant-view__timeline'
                      }
                    >
                      <div className="plant-view__timeline__header">
                        <Button
                          className="plant-view__timeline__collapser"
                          onClick={() => setTimelineCollapsed((current) => !current)}
                          icon={
                            timelineCollapsed ? <DoubleLeftOutlined /> : <DoubleRightOutlined />
                          }
                        ></Button>
                        {/* On a narrow window the heading belongs next to the collapser and
                            keeps its place whether the timeline is open or not - the styles
                            hide the timeline's own copy of it there. Side by side the column
                            is too narrow for a heading beside the button, so this one is
                            hidden instead. */}
                        <div className="plant-view__timeline__title title--feature">
                          {intl.formatMessage({id: 'myPlantTimeline'})}
                        </div>
                      </div>
                      {!timelineCollapsed && (
                        <PlantTimeEvents
                          currentPlantId={plant?.id}
                          currentPlantName={plant?.name}
                          timeEvents={plantTimeEvents}
                          plantToDos={plantToDos}
                          userToDoTypes={userToDoTypes}
                          userEventTypes={userEventTypes}
                          isPlantDeleted={plant.isPlantDeleted}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Spin>
    </div>
  )
}
